import { BasicDialog, BasicDialogActions } from '@/components/basicDialog';
import type { RolePlayResultFragment } from '@/gql/codegen/graphql';
import { Button } from '@/routes/_embaUserLayout/-components/Button';
import { LessonTitle } from '@/routes/_embaUserLayout/-components/LessonTitle';
import { getRolePlayName } from '@/utils/rolePlay';
import type React from 'react';
import CharacterImageCoffee from '../-assets/character-saki-coffee.png';
import CharacterImage from '../-assets/character-saki-normal.png';
import { CharacterMessage } from '../-components/CharacterMessage';
import { Conversation } from '../-components/Conversation';
import { Description } from '../-components/Description';
import { EvaluationDetails } from '../-components/EvaluationDetails';
import { EvaluationSummary } from '../-components/EvaluationSummary';
import { OverallEvaluation } from '../-components/OverallEvaluation';
import { RolePlayFeedback } from '../-components/RolePlayFeedback';
import styles from './index.module.scss';

export type RolePlayResultPageProps = {
  isTimeoutModalOpen: boolean;
  setIsTimeoutModalOpen: (open: boolean) => void;
  rolePlayResult: RolePlayResultFragment;
  onClickNext: () => void;
  chapterName: string;
  lessonName: string;
  onCreateRolePlayFeedback: (rating: number, comment: string) => Promise<void>;
  isFeedbackCreating: boolean;
  isFeedbackCreated: boolean;
};

const Page: React.FC<RolePlayResultPageProps> = ({
  rolePlayResult,
  onClickNext,
  isTimeoutModalOpen,
  setIsTimeoutModalOpen,
  chapterName,
  lessonName,
  onCreateRolePlayFeedback,
  isFeedbackCreating,
  isFeedbackCreated,
}) => {
  return (
    <>
      <LessonTitle chapterName={chapterName} lessonName={lessonName} />

      <div className={styles.container}>
        <div className={styles.content}>
          <h1 className={styles.title}>
            {getRolePlayName(rolePlayResult.isFirst)} 評価
          </h1>

          <Description isEvaluated={rolePlayResult.isEvaluated} />

          <div className={styles.summarySection}>
            <CharacterMessage
              variant="center"
              characterImageUrl={CharacterImage}
            >
              まずは、それぞれの評価項目ごとの達成率を確認してみましょう！
            </CharacterMessage>

            <EvaluationSummary
              title={rolePlayResult.learningPoint.title}
              firstPassRate={
                rolePlayResult.firstRolePlayResult.passRatePercentage
              }
              secondPassRate={
                rolePlayResult.secondRolePlayResult?.passRatePercentage
              }
              isFirst={rolePlayResult.isFirst}
              evaluationItemsWithResults={
                rolePlayResult.evaluationItemsWithResults
              }
            />
          </div>

          <div className={styles.evaluationSection}>
            <OverallEvaluation
              comment={rolePlayResult.evaluationComment}
              isEvaluated={rolePlayResult.isEvaluated}
            />

            <EvaluationDetails
              items={rolePlayResult.evaluationItemsWithResults}
              isEvaluated={rolePlayResult.isEvaluated}
            />
          </div>

          <div className={styles.conversationSection}>
            <CharacterMessage
              variant="center"
              characterImageUrl={CharacterImageCoffee}
            >
              会話ログを見てロールプレイを振り返ってみましょう！
            </CharacterMessage>

            <Conversation logs={rolePlayResult.sessionLogs} />
          </div>

          {rolePlayResult.isEvaluated && !rolePlayResult.hasFeedback && (
            <div className={styles.feedbackSection}>
              <div className={styles.feedbackDivider} />

              <RolePlayFeedback
                onCreateRolePlayFeedback={onCreateRolePlayFeedback}
                isFeedbackCreating={isFeedbackCreating}
                isFeedbackCreated={isFeedbackCreated}
              />
            </div>
          )}

          <div className={styles.buttonContainer}>
            <Button
              onClick={onClickNext}
              disabled={!rolePlayResult.isEvaluated}
            >
              次へ
            </Button>
          </div>
        </div>
      </div>
      <BasicDialog
        open={isTimeoutModalOpen}
        onOpenChange={setIsTimeoutModalOpen}
        showCloseButton={true}
        title="時間切れで終了しました"
        description="時間切れで終了しました"
      >
        <BasicDialogActions>
          <Button
            onClick={() => {
              setIsTimeoutModalOpen(false);
            }}
          >
            閉じる
          </Button>
        </BasicDialogActions>
      </BasicDialog>
    </>
  );
};

export default Page;
